import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import "../css/welcome.css";
import { useNavigate } from "react-router-dom"; 

const API_BASE = process.env.REACT_APP_API_BASE || "https://rfs2.infraconsoft.net.in/api";
const PRELOAD_MS = 3000;

const WelcomeEntry = () => {
  const [preloading, setPreloading] = useState(true);
  const [transitioning, setTransitioning] = useState(true);
  const navigate = useNavigate();     
  const [companyName, setCompanyName] = useState("Infraconsoft");
  const [companyLogo, setCompanyLogo] = useState(null);
  const [loadingSite, setLoadingSite] = useState(true);

  const [username, setUsername] = useState("");

  const [newsRows, setNewsRows] = useState([]);
  const [loadingNews, setLoadingNews] = useState(true);

  const [openIndex, setOpenIndex] = useState(null);

  // preload splash
  useEffect(() => {
    const t = setTimeout(() => setPreloading(false), PRELOAD_MS);
    return () => clearTimeout(t);
  }, []);

  // fade-in transition
  useEffect(() => {
    const t = setTimeout(() => setTransitioning(false), 900);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const u = localStorage.getItem("username") || "User";
    setUsername(u);
  }, []);

  const isActiveRow = (row) => {
    const approved = String(row?.Approve || "").trim().toLowerCase() === "yes";
    if (!approved) return false;
    if (row?.Expired_Date) {
      const exp = new Date(row.Expired_Date);
      const endOfDay = new Date(exp.getFullYear(), exp.getMonth(), exp.getDate(), 23, 59, 59, 999);
      if (new Date() > endOfDay) return false;
    }
    return true;
  };

  const normalizeRows = (rows = []) =>
    rows
      .filter(isActiveRow)
      .sort(
        (a, b) =>
          new Date(b.News_EntryDate || b.Expired_Date || "1970-01-01") -
          new Date(a.News_EntryDate || a.Expired_Date || "1970-01-01")
      )
      .map((r, i) => ({
        id: r.SlNo ?? i,
        heading: r.Heading || "—",
        content: r.Message || "",
        description: r.Description || "",
        date: r.News_EntryDate || r.Expired_Date || null,
      }));

  const newsData = useMemo(() => normalizeRows(newsRows), [newsRows]);

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
      : "";

  useEffect(() => {
    const loadSite = async () => {
      try {
        setLoadingSite(true);
        const { data } = await axios.get(`${API_BASE}/siteinfo`);
        if (data?.ok) {
          setCompanyName(data.name || "Infraconsoft");
          const logoUrl = data.logo
            ? `${data.logo}${data.logo.includes("?") ? "&" : "?"}ts=${Date.now()}`
            : null;
          setCompanyLogo(logoUrl);
        }
      } catch {
        /* keep defaults */
      } finally {
        setLoadingSite(false);
      }
    };

    const loadNews = async () => {
      try {
        setLoadingNews(true);
        const { data } = await axios.get(`${API_BASE}/flash_News_for_users`);
        setNewsRows(data?.success && Array.isArray(data.users) ? data.users : []);
      } catch {
        setNewsRows([]);
      } finally {
        setLoadingNews(false);
      }
    };

    loadSite();
    loadNews();
  }, []);

  const openModal = useCallback((idx) => setOpenIndex(idx), []);
  const closeModal = useCallback(() => setOpenIndex(null), []);

  useEffect(() => {
    const onKey = (e) => {
      if (openIndex === null) return;
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openIndex, closeModal]);

  if (preloading) {
    return (
      <div className="we-preload-full">
        <div className="we-preload-card">
          {companyLogo ? (
            <img className="we-preload-logo" src={companyLogo} alt="Logo" />
          ) : (
            <div className="we-preload-logo we-fallback">🏗</div>
          )}
          <div className="we-preload-line" />
          <div className="we-preload-sub">Loading your workspace…</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`we-surface ${transitioning ? "fade-in" : ""}`}>
      <div className="we-grid home">
        {/* LEFT: welcome card */}
        <header className="we-hero-centered we-fill">
          {companyLogo ? (
            <img className="we-logo-xxl" src={companyLogo} alt={`${companyName} logo`} />
          ) : (
            <div className="we-logo-xxl we-fallback">🏗</div>
          )}

          <h1 className="we-welcome-huge">{loadingSite ? "…" : companyName}</h1>

          <p className="we-greeting-lg">
            Welcome, <span className="we-user">{username || "User"}</span>!
          </p>

           <button
            className="we-enter-btn big"
            onClick={() => navigate("/home")}     // ⬅️ go to /home
          >
            🚀 Enter Workspace
          </button>
        </header>

        {/* RIGHT: flash news */}
        <main className="we-main we-fill">
          <section className="we-card we-fill">
            <div className="we-card-head">
              <h2 className="we-card-title">Flash News</h2>
              <span className="we-count">{newsData.length || 0}</span>
            </div>

            {loadingNews ? (
              <div className="we-skeleton">
                <div className="skl-line w80" />
                <div className="skl-line w60" />
                <div className="skl-line w70" />
              </div>
            ) : newsData.length === 0 ? (
              <div className="we-empty">No active news</div>
            ) : (
              <ul className="we-news-list scroll">
                {newsData.map((n, idx) => (
                  <li key={n.id} className="we-news-item">
                    <button type="button" className="we-news-btn" onClick={() => openModal(idx)}>
                      <div className="we-news-title">{n.heading}</div>
                      {n.date && <div className="we-news-date">{formatDate(n.date)}</div>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </main>
      </div>

      {/* MODAL */}
      {openIndex !== null && newsData[openIndex] && (
        <div
          className="we-modal"
          onClick={(e) => {
            if (e.target.classList.contains("we-modal")) closeModal();
          }}
        >
          <div className="we-modal-card glass">
            <div className="we-modal-head">
              <h3 className="we-modal-title brand">{newsData[openIndex].heading}</h3>
              <button className="we-close" onClick={closeModal} aria-label="Close">
                ✕
              </button>
            </div>
            {newsData[openIndex].date && (
              <div className="we-modal-date">{formatDate(newsData[openIndex].date)}</div>
            )}
            {newsData[openIndex].content && (
              <p className="we-modal-body">{newsData[openIndex].content}</p>
            )}
            {newsData[openIndex].description && (
              <div className="we-modal-desc">
                <span className="we-desc-ico">📋</span>
                <p>{newsData[openIndex].description}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WelcomeEntry;
